import { Button, Select, Skeleton } from '@mantine/core'
import classes from './RaceSummary.module.css'
import clsx from 'clsx'
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react'
import { useCompetitionRounds } from '../../matches/api'
import {
  Metric,
  useMetrics,
  DataPoint,
  useMetricsData,
} from '../../widgets/api'
import { FmtColored } from '../../widgets/format'
import { Suspense, useMemo, useState } from 'react'
import { Typography } from '../../common/typography'
import { RecentRace, useTeamRaceStats } from '../../races/api'
import { getRankSuffix } from '../../common/helpers'
import { Link } from 'react-router'

type RaceStats = {
  [kpi_key: string]: {
    [round: string]: {
      value: number
    }
  }
}

function SummaryColumn({
  race,
  kpis,
  component,
  statistic,
  data,
  competitionId,
}: {
  race: RecentRace
  kpis: Metric[]
  component: string
  statistic: string
  data: RaceStats
  competitionId: number
}) {
  return (
    <div className={classes.summary_column_container}>
      <Typography fc={{ light: 'black', dark: 'gray-3' }} fw={500} lh="125">
        {`R${race.round_number}`}
      </Typography>
      <Typography fc={{ light: 'black', dark: 'gray-3' }} lh="150">
        {new Date(race.round_start).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })}
      </Typography>
      <div
        className={clsx(
          classes.result,
          { [classes.firstPlace]: Number(race.result_rank) === 1 },
          { [classes.secondPlace]: Number(race.result_rank) === 2 },
          { [classes.thirdPlace]: Number(race.result_rank) === 3 }
        )}
      >
        <Typography
          fc={{
            light: 'white',
            dark:
              Number(race.result_rank === 1) || Number(race.result_rank === 2)
                ? 'gray-1'
                : 'gray-3',
          }}
          ta="center"
          ff="JetBrains Mono"
          lh="150"
        >
          {race.result_rank}
          <sup style={{ fontFamily: 'Barlow' }}>
            {getRankSuffix(race.result_rank)}
          </sup>
        </Typography>
      </div>

      {kpis.map((kpi) => (
        <Typography
          key={kpi.key}
          fw={500}
          lh="140"
          ff="JetBrains Mono"
          ta="center"
        >
          <FmtColored
            value={data[kpi.key]?.[race.round_number]?.value}
            metric={kpi.metric_id}
            component={component}
            statistic={statistic}
          />
        </Typography>
      ))}
      <Button
        component={Link}
        to={`/competitions/${competitionId}/races/${race.race_id}/summary`}
        className={classes.raceStatsButton}
      >
        <Typography
          fs="sm"
          fc={{ light: 'gray-0', dark: 'gray-0' }}
          lh="100"
          ta="center"
        >
          Race Stats
        </Typography>
      </Button>
    </div>
  )
}

export default function RaceSummary({
  summary_title,
  teamId,
  competitionId,
  isDesktop,
}: {
  summary_title: string
  teamId: number
  competitionId: number
  isDesktop: boolean
}) {
  const metrics = useMetrics()
  const kpis = metrics.data.filter((m) => m.is_kpi && m.is_used_in_matches)
  const { data: rounds } = useCompetitionRounds({
    competition_id: competitionId,
  })
  const [component, setComponent] = useState('percent')
  const DESKTOP_PAGE_SIZE = 6
  const MOBILE_PAGE_SIZE = 3

  const pageSize = isDesktop ? DESKTOP_PAGE_SIZE : MOBILE_PAGE_SIZE
  const lastRound = useMemo(() => {
    const lastEndedIndex = rounds.findLastIndex((r) => r.status === 'ended')
    return lastEndedIndex === -1 ? 0 : rounds[lastEndedIndex].round_number
  }, [isDesktop, rounds])

  const [roundEnd, setRoundEnd] = useState(lastRound)
  const roundStart = roundEnd - pageSize + 1
  const canGoNext = roundEnd < lastRound
  const canGoPrev = roundEnd - pageSize > 0

  const handleNext = () => {
    if (canGoNext) setRoundEnd((prev) => Math.min(prev + pageSize, lastRound))
  }

  const handlePrev = () => {
    if (canGoPrev) setRoundEnd((prev) => Math.max(prev - pageSize, pageSize))
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography fw={500} fc={{ light: 'black', dark: 'gray-3' }} lh="125">
          {summary_title}
        </Typography>
        <div className={classes.headerWrapper}>
          <div className={classes.pagination}>
            <IconChevronLeft
              aria-label="Previous page"
              className={classes.paginationIcon}
              onClick={handlePrev}
              data-pagination-disabled={!canGoPrev}
            />

            <IconChevronRight
              aria-label="Next page"
              className={classes.paginationIcon}
              onClick={handleNext}
              data-pagination-disabled={!canGoNext}
            />
          </div>

          <Select
            data={[
              { value: 'percent', label: 'Target (%)' },
              { value: 'actual', label: 'Actual' },
              { value: 'rank', label: 'Rank' },
            ]}
            value={component}
            onChange={(value) => setComponent(value || 'percent')}
            rightSection={<IconChevronDown className={classes.select_icon} />}
            classNames={{
              wrapper: classes.select_wrapper,
              input: classes.select_input,
              dropdown: classes.select_dropdown,
            }}
          />
        </div>
      </div>
      <div className={classes.grid_container}>
        <div className={classes.summary_label_container}>
          <Typography fc={{ light: 'black', dark: 'gray-3' }} lh="150">
            Race
          </Typography>
          <Typography fc={{ light: 'black', dark: 'gray-3' }} lh="150">
            Week
          </Typography>
          <Typography fc={{ light: 'black', dark: 'gray-3' }} lh="150">
            Result
          </Typography>
          {kpis.map((kpi) => (
            <Typography
              key={kpi.key}
              fc={{ light: 'black', dark: 'gray-3' }}
              lh="150"
            >
              {kpi.short_name || kpi.name}
            </Typography>
          ))}
        </div>
        <Suspense
          fallback={Array.from({ length: pageSize }).map((_, index) => (
            <div key={index} className={classes.summary_column_container}>
              <Skeleton height={16} width="100%" />
              <Skeleton height={16} width="100%" />
              <Skeleton height={32} width="100%" />
              {kpis.map((kpi) => (
                <Skeleton key={kpi.key} height={16} width="100%" />
              ))}
              <Skeleton height={32} width="100%" />
            </div>
          ))}
        >
          <RaceSummaryInner
            competitionId={competitionId}
            teamId={teamId}
            roundStart={roundStart}
            roundEnd={roundEnd}
            component={component}
            kpis={kpis}
          />
        </Suspense>
      </div>
    </div>
  )
}

interface InnerProps {
  competitionId: number
  teamId: number
  roundStart: number
  roundEnd: number
  component: string
  kpis: Metric[]
}

function RaceSummaryInner(props: InnerProps) {
  const { data: team_race_stats } = useTeamRaceStats({
    competition_id: props.competitionId,
    team_id: props.teamId,
    round_start: props.roundStart,
    round_end: props.roundEnd,
  })

  const kpis = props.kpis
  const apiComponent = props.component === 'rank' ? 'percent' : props.component
  const apiStatistic = props.component === 'rank' ? 'rank' : 'value'

  const { data } = useMetricsData({
    period_type: 'round',
    period_range: true,
    // Races are ordered DESC (newest first), so swap order for period range [start, end]
    periods:
      team_race_stats.recent_races.length > 0
        ? [
            team_race_stats.recent_races[
              team_race_stats.recent_races.length - 1
            ].round_start,
            team_race_stats.recent_races[0].round_start,
          ]
        : [],
    subject_type: 'team',
    subject_ids: [props.teamId || 0],
    metrics: kpis.map((kpi) => kpi.key),
    components: kpis.map(() => apiComponent),
    statistics: kpis.map(() => apiStatistic),
  })

  const racesData: RaceStats = useMemo(() => {
    const result: RaceStats = {}

    // Initialize result structure
    kpis.forEach((kpi) => {
      result[kpi.key] = {}
    })

    if (data) {
      // Create lookup maps for O(1) access instead of O(n) find operations
      const metricIdToKey = new Map(kpis.map((kpi) => [kpi.metric_id, kpi.key]))
      const periodToRoundNumber = new Map(
        team_race_stats.recent_races.map((race) => [
          race.round_start,
          race.round_number,
        ])
      )

      // Single pass through data with O(1) lookups
      data.forEach((dataPoint: DataPoint) => {
        const kpiKey = metricIdToKey.get(dataPoint.metric)
        if (!kpiKey) return

        const roundNumber = periodToRoundNumber.get(dataPoint.period)
        if (roundNumber === undefined) return

        result[kpiKey][roundNumber] = {
          value: dataPoint.value,
        }
      })
    }

    return result
  }, [data, kpis, team_race_stats.recent_races])

  // Reverse to display oldest first (R29 -> R34) since API returns DESC order
  return [...team_race_stats.recent_races]
    .reverse()
    .map((race: RecentRace) => (
      <SummaryColumn
        key={race.race_id}
        race={race}
        component={apiComponent}
        statistic={apiStatistic}
        kpis={kpis}
        data={racesData}
        competitionId={props.competitionId}
      />
    ))
}
